import { Directive, EventEmitter, Input, Output } from '@angular/core';

export type SortDirection = 'asc' | 'desc' | '';

export interface SortEvent {
  column: string;
  direction: SortDirection;
}

/**
 * Sortable Table Header Directive
 *
 * Makes table headers sortable with visual indicators.
 * Click to toggle between ascending and descending order.
 *
 * Usage:
 * ```html
 * <table class="table">
 *   <thead>
 *     <tr>
 *       <th sortable="name" (sort)="onSort($event)">Name</th>
 *       <th sortable="email" (sort)="onSort($event)">Email</th>
 *       <th>Actions</th> <!-- Non-sortable -->
 *     </tr>
 *   </thead>
 * </table>
 * ```
 *
 * In component:
 * ```typescript
 * onSort(event: SortEvent) {
 *   // event.column = 'name' or 'email'
 *   // event.direction = 'asc' or 'desc'
 *   this.sortColumn = event.column;
 *   this.sortDirection = event.direction;
 *   this.loadData();
 * }
 * ```
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'th[sortable]',
  standalone: false,
  host: {
    '[class.sortable]': 'true',
    '[class.asc]': 'direction === "asc"',
    '[class.desc]': 'direction === "desc"',
    '(click)': 'rotate()'
  }
})
export class SortableHeaderDirective {
  /** Column name to sort by */
  @Input() sortable = '';

  /** Current sort direction */
  @Input() direction: SortDirection = '';

  /** Emits when sort changes */
  @Output() sort = new EventEmitter<SortEvent>();

  /** Toggle sort direction on click */
  rotate(): void {
    this.direction = this.direction === 'asc' ? 'desc' : 'asc';
    this.sort.emit({
      column: this.sortable,
      direction: this.direction
    });
  }
}
